import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../../../../api/client';
import { useToastStore } from '../../../../stores/toastStore';
import { useAbandonDraft } from '../hooks';

interface AbandonDraftButtonProps {
  draftId: string;
}

/**
 * Deferral D6 UI — confirms then abandons the draft. The backend transitions
 * the draft to <c>Abandoned</c> and triggers tenant-scoped blob cleanup; we
 * navigate the user back to the list once that succeeds.
 */
export function AbandonDraftButton({ draftId }: AbandonDraftButtonProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const abandon = useAbandonDraft();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);

  const handleConfirm = (): void => {
    abandon.mutate(draftId, {
      onSuccess: () => {
        pushToast({
          tone: 'success',
          title: 'Descubrimiento descartado',
          description: 'Las muestras se eliminaron del almacenamiento.',
        });
        setOpen(false);
        navigate('/document-types');
      },
      onError: (err) => {
        const message =
          err instanceof ApiError ? err.message : 'No se pudo descartar el descubrimiento.';
        pushToast({ tone: 'error', title: 'Error al descartar', description: message });
      },
    });
  };

  return (
    <>
      <button
        type="button"
        className="btn-secondary text-xs"
        onClick={() => setOpen(true)}
        disabled={abandon.isPending}
      >
        Abandonar descubrimiento
      </button>
      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar abandono del descubrimiento"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="card max-w-md space-y-4 p-5">
            <h2 className="text-base font-semibold text-slate-900">
              ¿Abandonar este descubrimiento?
            </h2>
            <p className="text-sm text-slate-600">
              Los archivos PDF subidos se eliminarán del almacenamiento. Esta
              acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setOpen(false)}
                disabled={abandon.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirm}
                disabled={abandon.isPending}
              >
                {abandon.isPending ? 'Descartando…' : 'Sí, abandonar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
